import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { createHoldingChildCompany } from "../../../../api/operatorHoldings";
import { getCommunes, getRegions } from "../../../../api/contracts";
import { useCountry } from "../../../../context/CountryContext";
import { Button } from "../../../../components/ui/Button";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { Input } from "../../../../components/ui/Input";
import { Modal } from "../../../../components/ui/Modal";
import { Select } from "../../../../components/ui/Select";
import { Switch } from "../../../../components/ui/Switch";
import {
  ADDRESS_TYPES,
  SENDER_INTEGRATIONS,
  WEEK_DAYS,
  buildChildBody,
  childErrorText,
  emptyAddress,
  initialChildForm,
  operationsCountryOf,
  validateChildForm,
} from "./holdingChildForm";

function Field({ label, error, children, htmlFor }) {
  return (
    <div className="space-y-1">
      <FieldLabel htmlFor={htmlFor}>{label}</FieldLabel>
      {children}
      {error && <p className="text-xs text-status-error">{error}</p>}
    </div>
  );
}

function TextField({ id, label, value, onChange, error, ...rest }) {
  return (
    <Field label={label} error={error} htmlFor={id}>
      <Input
        id={id}
        value={value}
        invalid={Boolean(error)}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </Field>
  );
}

/**
 * Alta de una empresa NUEVA dentro del holding, desde su ficha. contracts la crea con el
 * contrato del holding replicado (el activo y vigente más reciente de sus empresas, con inicio
 * hoy), con o sin integración, y la deja vinculada: no hace falta crear el contrato ni
 * vincularla a mano.
 *
 * El RUT es el correlativo del holding salvo que se elija uno propio. El país es el del
 * operador.
 *
 * @param {{ open: boolean, holding: object, onClose: () => void,
 *           onCreated: (result: object) => void }} props
 *   `holding` es un elemento de GET contracts/holdings; `onCreated` recibe la respuesta del POST.
 */
export default function CreateHoldingCompanyModal({ open, holding, onClose, onCreated }) {
  const { t } = useTranslation();
  const formId = useId();
  const baseId = useId();
  const { countryId, countryCode } = useCountry();
  const [form, setForm] = useState(() => initialChildForm(holding));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [regions, setRegions] = useState([]);
  const [communesByRegion, setCommunesByRegion] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(initialChildForm(holding));
    setErrors({});
    setSubmitError("");
    setSaving(false);
  }, [open, holding]);

  useEffect(() => {
    if (!open || !countryId) return undefined;
    let alive = true;
    getRegions({ countryId })
      .then((data) => {
        if (alive && Array.isArray(data)) setRegions(data);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [open, countryId]);

  // Las direcciones guardan región y comuna por NOMBRE (como el resto de las empresas): el
  // código solo sirve para pedir las comunas.
  const regionCodeOf = (name) => regions.find((r) => r.name === name)?.code;
  const neededRegionCodes = useMemo(
    () =>
      [...new Set(form.shippingAddresses.map((a) => regions.find((r) => r.name === a.state)?.code))].filter(
        (code) => code && !communesByRegion[code],
      ),
    [form.shippingAddresses, regions, communesByRegion],
  );

  useEffect(() => {
    if (!neededRegionCodes.length || !countryId) return undefined;
    let alive = true;
    Promise.all(
      neededRegionCodes.map((code) =>
        getCommunes({ countryId, regionId: code }).then((data) => ({ code, data })),
      ),
    ).then((results) => {
      if (!alive) return;
      setCommunesByRegion((prev) => {
        const next = { ...prev };
        for (const { code, data } of results) if (Array.isArray(data)) next[code] = data;
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [neededRegionCodes, countryId]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setContact = (who, key, value) =>
    setForm((prev) => ({ ...prev, [who]: { ...prev[who], [key]: value } }));
  const setAddress = (index, patch) =>
    setForm((prev) => ({
      ...prev,
      shippingAddresses: prev.shippingAddresses.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    }));
  const toggleDay = (day) =>
    setForm((prev) => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter((d) => d !== day)
        : WEEK_DAYS.map((d) => d.value).filter((d) => d === day || prev.workingDays.includes(d)),
    }));

  const err = (key) => (errors[key] ? t(errors[key]) : undefined);
  const taxIdLabel = countryCode === "AR" ? t("CUIT") : t("RUT");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const found = validateChildForm(form, countryCode);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setSubmitError(t("Revisa los campos marcados."));
      return;
    }
    setSaving(true);
    setSubmitError("");
    try {
      const { data } = await createHoldingChildCompany(
        holding.id,
        buildChildBody(form, operationsCountryOf(countryCode)),
      );
      onCreated?.(data);
    } catch (error) {
      setSubmitError(t(childErrorText(error)));
    } finally {
      setSaving(false);
    }
  };

  const contactFields = (who, { withRut }) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <TextField
        id={`${baseId}-${who}-name`}
        label={t("Nombre")}
        value={form[who].name}
        onChange={(v) => setContact(who, "name", v)}
        error={err(`${who}Name`)}
      />
      <TextField
        id={`${baseId}-${who}-email`}
        label={t("Correo")}
        type="email"
        value={form[who].email}
        onChange={(v) => setContact(who, "email", v)}
        error={err(`${who}Email`)}
      />
      <TextField
        id={`${baseId}-${who}-phone`}
        label={t("Teléfono")}
        value={form[who].phone}
        onChange={(v) => setContact(who, "phone", v)}
        error={err(`${who}Phone`)}
      />
      {withRut ? (
        <TextField
          id={`${baseId}-${who}-rut`}
          label={t("RUT (opcional)")}
          value={form[who].rut}
          onChange={(v) => setContact(who, "rut", v)}
          error={err(`${who}Rut`)}
        />
      ) : (
        <TextField
          id={`${baseId}-${who}-role`}
          label={t("Cargo (opcional)")}
          value={form[who].role}
          onChange={(v) => setContact(who, "role", v)}
        />
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      size="lg"
      title={t("Crear empresa en el holding {{holding}}", { holding: holding?.name || t("Sin nombre") })}
      description={t("Se crea con el contrato del holding (inicio hoy) y queda vinculada al holding.")}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button type="submit" form={formId} loading={saving} iconLeft={<Plus className="h-4 w-4" />}>
            {t("Crear empresa")}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-5" noValidate>
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-fg">{t("Empresa")}</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              id={`${baseId}-name`}
              label={t("Nombre")}
              value={form.name}
              onChange={(v) => set("name", v)}
              error={err("name")}
            />
            <TextField
              id={`${baseId}-legal`}
              label={t("Razón social")}
              value={form.legalName}
              onChange={(v) => set("legalName", v)}
              error={err("legalName")}
            />
          </div>
          <TextField
            id={`${baseId}-fiscal`}
            label={t("Dirección fiscal")}
            value={form.fiscalAddress}
            onChange={(v) => set("fiscalAddress", v)}
            error={err("fiscalAddress")}
          />
          <div className="space-y-2">
            <Switch
              checked={form.ownTaxId}
              onChange={(v) => set("ownTaxId", v)}
              label={t("Usar {{taxId}} propio", { taxId: taxIdLabel })}
            />
            {form.ownTaxId ? (
              <TextField
                id={`${baseId}-taxid`}
                label={taxIdLabel}
                value={form.businessIdentifier}
                onChange={(v) => set("businessIdentifier", v)}
                error={err("businessIdentifier")}
              />
            ) : (
              <p className="text-xs text-fg-muted">
                {t("Se asigna el correlativo del holding ({{rut}} + número).", {
                  rut: holding?.businessIdentifier || "—",
                })}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr]">
            <TextField
              id={`${baseId}-pickup`}
              label={t("Hora de retiro")}
              type="time"
              value={form.pickUpTime}
              onChange={(v) => set("pickUpTime", v)}
              error={err("pickUpTime")}
            />
            <Field label={t("Días de operación")} error={err("workingDays")}>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("Días de operación")}>
                {WEEK_DAYS.map((day) => {
                  const on = form.workingDays.includes(day.value);
                  return (
                    <Button
                      key={day.value}
                      size="sm"
                      variant={on ? "primary" : "outline"}
                      aria-pressed={on}
                      onClick={() => toggleDay(day.value)}
                    >
                      {t(day.label)}
                    </Button>
                  );
                })}
              </div>
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-fg">{t("Direcciones de retiro")}</legend>
          {form.shippingAddresses.map((a, i) => {
            const communes = communesByRegion[regionCodeOf(a.state)] ?? [];
            return (
              <div key={i} className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-fg-muted">
                    {t("Dirección {{n}}", { n: i + 1 })}
                  </p>
                  {form.shippingAddresses.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t("Quitar dirección {{n}}", { n: i + 1 })}
                      onClick={() =>
                        set(
                          "shippingAddresses",
                          form.shippingAddresses.filter((_, j) => j !== i),
                        )
                      }
                      iconLeft={<Trash2 className="h-4 w-4" />}
                    />
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px]">
                  <TextField
                    id={`${baseId}-addr-${i}`}
                    label={t("Dirección")}
                    value={a.address}
                    onChange={(v) => setAddress(i, { address: v })}
                    error={err(`address_${i}`)}
                  />
                  <Select
                    label={t("Tipo")}
                    value={a.type}
                    onChange={(e) => setAddress(i, { type: e.target.value })}
                    options={ADDRESS_TYPES.map((type) => ({ value: type, label: t(type) }))}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Select
                    label={t("Región")}
                    value={a.state}
                    placeholder={t("Elige una región")}
                    error={err(`state_${i}`)}
                    onChange={(e) => setAddress(i, { state: e.target.value, municipality: "" })}
                    options={regions.map((r) => ({ value: r.name, label: r.name }))}
                  />
                  <Select
                    label={t("Comuna")}
                    value={a.municipality}
                    placeholder={t("Elige una comuna")}
                    error={err(`municipality_${i}`)}
                    disabled={!a.state}
                    onChange={(e) => setAddress(i, { municipality: e.target.value })}
                    options={communes.map((c) => ({ value: c.name, label: c.name }))}
                  />
                  <TextField
                    id={`${baseId}-phone-${i}`}
                    label={t("Teléfono")}
                    value={a.contactPhone}
                    onChange={(v) => setAddress(i, { contactPhone: v })}
                    error={err(`contactPhone_${i}`)}
                  />
                </div>
              </div>
            );
          })}
          <Button
            size="sm"
            variant="outline"
            onClick={() => set("shippingAddresses", [...form.shippingAddresses, emptyAddress()])}
            iconLeft={<Plus className="h-4 w-4" />}
          >
            {t("Agregar dirección")}
          </Button>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-fg">{t("Contacto principal")}</legend>
          {contactFields("primary", { withRut: false })}
          <Switch
            checked={form.withBilling}
            onChange={(v) => set("withBilling", v)}
            label={t("Agregar contacto de facturación")}
          />
          {form.withBilling && contactFields("billing", { withRut: true })}
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-fg">{t("Integración")}</legend>
          <Switch
            checked={form.withIntegration}
            onChange={(v) => set("withIntegration", v)}
            label={t("Crear con integración")}
          />
          {form.withIntegration && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label={t("Plataforma")}
                value={form.integrationName}
                onChange={(e) => set("integrationName", e.target.value)}
                options={SENDER_INTEGRATIONS.map((name) => ({ value: name, label: name }))}
              />
              <TextField
                id={`${baseId}-sender`}
                label={t("sender_id")}
                value={form.senderId}
                onChange={(v) => set("senderId", v)}
                error={err("senderId")}
              />
            </div>
          )}
        </fieldset>

        {submitError && (
          <p className="text-sm text-status-error" role="alert">
            {submitError}
          </p>
        )}
      </form>
    </Modal>
  );
}
